import { type Component, computed, type Ref, unref } from "vue";
import { type NavigationFailure, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { BILLING_PARTY_INSTITUTION, ORDER_STATUS } from "@/constants";
import AboutYou from "../apply/AboutYou.vue";
import AccountCreationStep from "../apply/AccountCreationStep.vue";
import Checkout from "../apply/Checkout.vue";
import GroupVisa from "../apply/GroupVisa.vue";
import LearnerInformation from "../apply/LearnerInformation.vue";
import NextSteps from "../apply/NextSteps.vue";
import Review from "../apply/Review.vue";
import ReviewAndSubmit from "../apply/ReviewAndSubmit.vue";
import { requiredLearnerInformationFields } from "../apply/utils";
import { POSSIBLE_STEP_NAMES } from "../constants";
import isGroupVisaEligible from "../utils/isGroupVisaEligible";
import { areAllFieldsInObject, getSingleParam } from "../utils/utils";

type StepResolutionState = "pending" | "complete" | "incomplete";
type StepsResolutionStatus = "pending" | "ready";
type StepActivityState = "active" | "inactive" | "pending";

interface ApplicationStep {
  slug: string;
  title: string;
  component: Component;
  activityState: () => StepActivityState;
  isSelectable: () => boolean;
  stepStatus: (allPreviousActiveStepsComplete?: boolean | undefined) => StepResolutionState;
  isComplete: () => boolean;
}

interface ActiveStep extends ApplicationStep {
  props: {
    application: Ref<Record<string, any>>;
    setCurrentStep: (slug: string) => Promise<NavigationFailure | void>;
    goToNextStep: () => void;
  };
}

interface EvaluatedStep {
  completionBySlug: Record<string, StepResolutionState>;
  firstIncompleteStep?: ApplicationStep;
  stepsResolutionStatus: StepsResolutionStatus;
}

const ABOUT_YOU_REQUIRED_FIELDS = Object.freeze<string[]>([
  "legal_first_name",
  "last_name",
  "birthdate",
  "gender_identity",
  "primary_citizenship_country_id",
]);

export const useApplicationSteps = (application: Ref<Record<string, any>> | Record<string, any>) => {
  const router = useRouter();
  const route = useRoute();
  const store = useStore();

  const {
    appliedAgreement,
    applicationId,
    applicationOrders,
    learnerInformationSubmission,
    hasPaymentForApplicationId,
    applicationSubmissions,
    profile,
    primaryOrder,
    session,
    noApplicationFeeFlow,
    orderIsInDraft,
  } = unref(application);

  const isLoggedIn = computed(() => store.getters.isLoggedIn);
  const isResolved = (value: unknown): boolean => value !== undefined && value !== null;
  const profileIsResolved = computed(() => isResolved(profile.value));
  const sessionIsResolved = computed(() => isResolved(session.value));
  const applicationOrdersAreResolved = computed(() => !applicationId.value || Array.isArray(applicationOrders?.value));
  const applicationSubmissionsAreResolved = computed(
    () => !applicationId.value || isResolved(applicationSubmissions.value),
  );
  const paymentStateIsResolved = computed(() => !applicationId.value || isResolved(hasPaymentForApplicationId.value));

  const institutionPays = computed(() => {
    return appliedAgreement.value?.applicationFee === BILLING_PARTY_INSTITUTION;
  });

  const setCurrentStep = async (slug: string) => {
    const newStepIndex = activeSteps.value.findIndex((step) => step.slug === slug);
    const newStep = activeSteps.value[newStepIndex];
    if (newStep?.isSelectable() || newStepIndex === 0) {
      const to = {
        params: { ...route.params, step: newStep.slug },
        query: route.query,
      };
      return await router.push(to);
    }
    // if desired step is not yet selectable, go to the first
    // selectable step in the sequence
    setCurrentStep(activeSteps.value[newStepIndex - 1].slug);
  };

  const goToNextStep = async (): Promise<void> => {
    const index = activeSteps.value.findIndex((step) => step.slug === currentStep.value?.slug);
    //The returned index is not the last element in the steps
    const nextStep = activeSteps.value[index + 1] ?? currentStep.value;
    if (nextStep?.isSelectable()) {
      await setCurrentStep(nextStep.slug);
    } else {
      const lastIncompleteStep = activeSteps.value.find((step) => !step.isComplete());
      if (lastIncompleteStep) await setCurrentStep(lastIncompleteStep.slug);
    }
  };

  const step = computed<string | undefined>(() => getSingleParam(route.params?.step));

  const stepProps = {
    application: computed<Record<string, any>>(() => unref(application)),
    setCurrentStep,
    goToNextStep,
  };

  const activeSteps = computed<ActiveStep[]>(() => {
    const filteredSteps = possibleSteps.filter((possibleStep) => possibleStep.activityState() === "active");
    const steps: ActiveStep[] = filteredSteps.map<ActiveStep>((step) => {
      return { ...step, props: stepProps };
    });
    return steps;
  });

  const currentStep = computed<ActiveStep>(
    () => activeSteps.value.find((activeStep) => step.value === activeStep.slug) ?? activeSteps.value[0],
  );

  const aboutYouInformationIsComplete = (): boolean =>
    Boolean(primaryOrder?.value) && ABOUT_YOU_REQUIRED_FIELDS.every((field) => Boolean(profile.value?.[field]));

  const resolveAboutYouStatus = (): StepResolutionState => {
    if (applicationId.value && (!applicationOrdersAreResolved.value || !profileIsResolved.value)) {
      return "pending";
    }

    return aboutYouInformationIsComplete() ? "complete" : "incomplete";
  };

  const learnerInformationIsComplete = (): boolean => {
    if (!(Boolean(session.value) && Boolean(profile.value))) {
      return false;
    }
    const fieldsFilled = areAllFieldsInObject(
      requiredLearnerInformationFields(session.value, profile.value.learner_type_id),
      profile.value,
    );
    if (!session.value.requires_application) {
      // No application, so don't need status of "studentapplicationstage2step1"
      return Boolean(fieldsFilled);
    }
    return Boolean(fieldsFilled && learnerInformationSubmission.value);
  };

  const resolveLearnerInformationStatus = (): StepResolutionState => {
    if (!sessionIsResolved.value || !profileIsResolved.value || !isResolved(profile.value?.learner_type_id)) {
      return "pending";
    }

    if (session.value.requires_application && !applicationSubmissionsAreResolved.value) {
      return "pending";
    }

    return learnerInformationIsComplete() ? "complete" : "incomplete";
  };

  const resolveGroupVisaActivity = (): StepActivityState => {
    if (!profileIsResolved.value || !sessionIsResolved.value) {
      return "pending";
    }

    return isGroupVisaEligible(profile.value, session.value) ? "active" : "inactive";
  };

  const checkoutPaymentAndOrderComplete = (): boolean => {
    const paymentSubmissions: any[] = applicationSubmissions.value?.["paymentform"] ?? [];
    const usedPromoCode: boolean = paymentSubmissions.some(
      (submission) => submission.data.promotionCode && submission.data.promotionCode.trim().length > 0,
    );

    const applicationDepositIsSettled: boolean =
      Boolean(hasPaymentForApplicationId.value) || institutionPays.value || usedPromoCode;
    return applicationDepositIsSettled && !orderIsInDraft.value;
  };

  const checkoutIsComplete = (): boolean => {
    // Don't simplify with checkoutPaymentAndOrderComplete, it will produce stale data
    return checkoutPaymentAndOrderComplete() && allPreviousStepsAreCompleted("checkout");
  };

  const resolveCheckoutStatus = (allPreviousActiveStepsComplete: boolean = false): StepResolutionState => {
    if (!allPreviousActiveStepsComplete) {
      return "incomplete";
    }

    if (
      applicationId.value &&
      (!applicationOrdersAreResolved.value ||
        !applicationSubmissionsAreResolved.value ||
        !paymentStateIsResolved.value)
    ) {
      return "pending";
    }

    return checkoutPaymentAndOrderComplete() ? "complete" : "incomplete";
  };

  const allPreviousStepsAreCompleted = (slug: string): boolean => {
    const currentIndex = activeStepSlugs.value.findIndex((stepSlug) => stepSlug === slug);
    if (currentIndex === -1) return false;
    // first step always will be selectable
    if (currentIndex === 0) return true;
    const previousSteps = activeStepSlugs.value.slice(0, currentIndex);
    return previousSteps.every((stepSlug) => stepEvaluation.value.completionBySlug[stepSlug] === "complete");
  };

  const resolvePreviousStepsStatus = (allPreviousActiveStepsComplete: boolean = false): StepResolutionState => {
    return allPreviousActiveStepsComplete ? "complete" : "incomplete";
  };

  const resolveReviewAndSubmitStatus = (): StepResolutionState => {
    if (applicationId.value && (!applicationOrdersAreResolved.value || !applicationSubmissionsAreResolved.value)) {
      return "pending";
    }

    const hasFormSubmission = Boolean(
      applicationSubmissions.value?.["paymentform"]?.length ||
        applicationSubmissions.value?.["institutionpayment"]?.length,
    );

    return !orderIsInDraft.value && hasFormSubmission ? "complete" : "incomplete";
  };

  const possibleSteps: ApplicationStep[] = [
    {
      slug: "account-creation",
      title: POSSIBLE_STEP_NAMES.accountCreation,
      component: AccountCreationStep,
      activityState: () => (!isLoggedIn.value ? "active" : "inactive"),
      isSelectable: () => true,
      stepStatus: () => (applicationId.value ? "complete" : "incomplete"),
      isComplete: () => Boolean(applicationId.value),
    },
    {
      slug: "about-you",
      title: POSSIBLE_STEP_NAMES.aboutYou,
      component: AboutYou,
      activityState: () => "active",
      isSelectable: () => Boolean(applicationId.value),
      stepStatus: resolveAboutYouStatus,
      isComplete: aboutYouInformationIsComplete,
    },
    {
      slug: "learner-info",
      title: POSSIBLE_STEP_NAMES.learnerInformation,
      component: LearnerInformation,
      activityState: () => "active",
      isSelectable: aboutYouInformationIsComplete,
      stepStatus: resolveLearnerInformationStatus,
      isComplete: learnerInformationIsComplete,
    },
    {
      slug: "group-visa",
      title: POSSIBLE_STEP_NAMES.groupVisa,
      component: GroupVisa,
      activityState: resolveGroupVisaActivity,
      isSelectable: learnerInformationIsComplete,
      stepStatus: resolveLearnerInformationStatus,
      isComplete: learnerInformationIsComplete,
    },
    {
      slug: "review",
      title: POSSIBLE_STEP_NAMES.review,
      component: Review,
      activityState: () => (noApplicationFeeFlow.value ? "inactive" : "active"),
      isSelectable: () => allPreviousStepsAreCompleted("review"),
      stepStatus: resolvePreviousStepsStatus,
      isComplete: () => allPreviousStepsAreCompleted("review"),
    },
    {
      slug: "review-and-submit",
      title: POSSIBLE_STEP_NAMES.reviewAndSubmit,
      component: ReviewAndSubmit,
      activityState: () => (noApplicationFeeFlow.value ? "active" : "inactive"),
      isSelectable: () => allPreviousStepsAreCompleted("review-and-submit"),
      stepStatus: resolveReviewAndSubmitStatus,
      isComplete: () => allPreviousStepsAreCompleted("review-and-submit"),
    },
    {
      slug: "checkout",
      title: institutionPays.value ? POSSIBLE_STEP_NAMES.submitInstitution : POSSIBLE_STEP_NAMES.checkout,
      component: Checkout,
      activityState: () => (noApplicationFeeFlow.value ? "inactive" : "active"),
      isSelectable: () => allPreviousStepsAreCompleted("checkout"),
      stepStatus: resolveCheckoutStatus,
      isComplete: checkoutIsComplete,
    },
    {
      slug: "next-steps",
      title: POSSIBLE_STEP_NAMES.nextSteps,
      component: NextSteps,
      activityState: () => "active",
      isSelectable: () => allPreviousStepsAreCompleted("next-steps"),
      stepStatus: resolvePreviousStepsStatus,
      isComplete: () => allPreviousStepsAreCompleted("next-steps"),
    },
  ];

  const activeStepSlugs = computed<string[]>(() =>
    possibleSteps
      .filter((possibleStep) => possibleStep.activityState() === "active")
      .map((possibleStep) => possibleStep.slug),
  );

  const stepEvaluation = computed<EvaluatedStep>(() => {
    const completionBySlug: Record<string, StepResolutionState> = {};
    let allPreviousActiveStepsComplete = true;

    for (const possibleStep of possibleSteps) {
      const activity = possibleStep.activityState();
      if (activity === "pending") {
        return {
          completionBySlug,
          firstIncompleteStep: undefined,
          stepsResolutionStatus: "pending",
        };
      }

      if (activity === "inactive") {
        continue;
      }

      const stepStatus = possibleStep.stepStatus(allPreviousActiveStepsComplete);
      completionBySlug[possibleStep.slug] = stepStatus;

      if (stepStatus === "pending") {
        return {
          completionBySlug,
          firstIncompleteStep: undefined,
          stepsResolutionStatus: "pending",
        };
      }

      if (stepStatus === "incomplete") {
        return {
          completionBySlug,
          firstIncompleteStep: possibleStep,
          stepsResolutionStatus: "ready",
        };
      }

      allPreviousActiveStepsComplete = stepStatus === "complete";
    }

    return {
      completionBySlug,
      firstIncompleteStep: undefined,
      stepsResolutionStatus: "ready",
    };
  });

  const firstIncompleteStep = computed<ApplicationStep | undefined>(() => stepEvaluation.value.firstIncompleteStep);
  const stepsResolutionStatus = computed<StepsResolutionStatus>(() => stepEvaluation.value.stepsResolutionStatus);

  return {
    allPossibleSteps: possibleSteps,
    activeSteps,
    firstIncompleteStep,
    currentStep,
    setCurrentStep,
    goToNextStep,
    stepsResolutionStatus,
  };
};
